import React, { useEffect, useRef } from 'react';
import { ArrowRight, Play } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const HeroSection = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const heroRef = useRef(null);
  const isLight = theme === 'light';

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="hero"
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ paddingTop: '80px' }}
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxBSSUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzY2NzkyODc3fDA&ixlib=rb-4.1.0&q=85"
          alt="Hero Background"
          className="w-full h-full object-cover opacity-30"
        />
        <div
          className="absolute inset-0"
          style={{
            background: isLight
              ? 'linear-gradient(180deg, rgba(245, 245, 247, 0.75) 0%, rgba(245, 245, 247, 0.97) 100%)'
              : 'linear-gradient(180deg, rgba(11, 14, 19, 0.7) 0%, rgba(11, 14, 19, 0.95) 100%)',
          }}
        />
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 rounded-full blur-3xl opacity-20" style={{ background: '#F97316' }} />
      <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full blur-3xl opacity-20" style={{ background: '#A64425' }} />

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center space-y-8">
        <div className="space-y-4">
          <h1
            className="text-5xl md:text-7xl font-bold tracking-tight text-foreground-primary"
          >
            Intelligent Machines.
            <br />
            <span style={{ color: '#F97316' }}>Infinite Possibilities.</span>
          </h1>
          <p
            className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed text-foreground-secondary"
          >
            From autonomous pipe inspection to precision field measurement — we build intelligent
            machines that transform how industries operate.
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
          <Button
            size="lg"
            className="px-8 py-6 text-lg font-semibold transition-smooth group"
            style={{
              background: '#F97316',
              color: '#FFFFFF',
            }}
            onClick={() => navigate('/contact')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 0 40px rgba(249, 115, 22, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Get Started
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="px-8 py-6 text-lg font-semibold glass-effect transition-smooth group"
            style={{
              color: isLight ? '#1D1D1F' : '#FFFFFF',
              borderColor: isLight ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.2)',
            }}
            onClick={() => {
              const element = document.getElementById('solutions');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.borderColor = '#F97316';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.borderColor = isLight ? 'rgba(0, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.2)';
            }}
          >
            <Play className="mr-2" size={20} />
            Learn More
          </Button>
        </div>

        {/* Stats */}
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 pt-12">
          {[
            { value: '2', label: 'Product Lines' },
            { value: '10+', label: 'Industries' },
            { value: '100%', label: 'Made in India' },
            { value: 'AI', label: 'Powered' },
          ].map((stat, index) => (
            <div key={index} className="flex items-center gap-3">
              {index > 0 && <span className={`hidden md:block w-px h-10 -ml-4 mr-1 ${isLight ? 'bg-black/10' : 'bg-white/10'}`} />}
              <span className="text-2xl md:text-3xl font-bold" style={{ color: '#F97316' }}>{stat.value}</span>
              <span className="text-sm text-foreground-secondary">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

    </section>
  );
};

export default HeroSection;